import classNames from 'classnames';
import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import TwoColumnRow from '../app/layout/TwoColumnRow';
import useIsLayoutSize from '../post/utils/useIsLayoutSize';
import { LayoutSize } from '../post/utils/useLayoutSize';
import * as styles from './BannerEditor.module.scss';
import type { Banner } from './BannerModalContent';
import BannerModalContent from './BannerModalContent';
import Button from './Button';
import type { ImageEditorRootRef } from './ImageEditor';
import ImageEditor, { IMAGE_EDITOR_ACCEPT } from './ImageEditor';
import { BANNER_IMAGE_LIBRARY } from './imageLibraries';

// We multiply width and height by 2 to save higher resolution
// images that look good on retina displays.
const BANNER_HEIGHT = parseInt(styles.bannerHeight, 10) * 2;
const BANNER_WIDTH = parseInt(styles.bannerWidth, 10) * 2;

export interface BannerEditorRef {
  getBannerFile(): Promise<File | null>;
}

interface BannerEditorProps {
  currentBannerUrl?: string | null;
  state: 'viewing' | 'editing' | 'saving';
  onBannerChange(banner: Banner | null): void;
  banner: Banner | null;
}

const BannerEditor = React.forwardRef<BannerEditorRef, BannerEditorProps>(
  ({ currentBannerUrl, state, onBannerChange, banner }, ref) => {
    const [isPositioning, setIsPositioning] = useState(false);

    useEffect(() => {
      if (state !== 'editing' || banner == null) {
        setIsPositioning(false);
      }
    }, [currentBannerUrl, state, banner]);

    const imageEditorRef = useRef<ImageEditorRootRef>(null);

    useImperativeHandle(ref, () => ({
      getBannerFile() {
        return imageEditorRef.current
          ? imageEditorRef.current.getFile()
          : Promise.resolve(null);
      },
    }));

    const [isBannerPopOverOpen, setIsBannerPopOverOpen] = useState(false);

    const handleBannerChange = (newBanner: Banner | null) => {
      if (newBanner) {
        setIsPositioning(true);
      }
      setIsBannerPopOverOpen(false);
      onBannerChange(newBanner);
    };

    const hasImage =
      state === 'viewing' ? currentBannerUrl != null : banner != null;

    const isDesktop = useIsLayoutSize(LayoutSize.desktop);

    return (
      <div
        className={classNames(styles.container, {
          [styles.isEditing]: state === 'editing',
          [styles.hasImage]: hasImage,
        })}
      >
        {banner ? (
          <ImageEditor.Root
            ref={imageEditorRef}
            value={banner}
            src={currentBannerUrl ?? null}
            height={BANNER_HEIGHT}
            width={BANNER_WIDTH}
            positioning={isPositioning}
            onDropImage={(image) => handleBannerChange({ image })}
            dropZoneDisabled={state !== 'editing'}
            onDropZoneClick={() => {
              setIsBannerPopOverOpen((isOpen) => !isOpen);
            }}
          />
        ) : (
          <ImageEditor.DropZone
            onDropImage={(image) => handleBannerChange({ image })}
            onClick={() => setIsBannerPopOverOpen((isOpen) => !isOpen)}
          >
            <div className={styles.emptyDropZoneInfo}>
              <div>Personalize with a banner image</div>
            </div>
          </ImageEditor.DropZone>
        )}

        {state !== 'viewing' && (
          <div className={styles.controlsLayout}>
            <TwoColumnRow
              full={
                <div className={styles.controlsContainer}>
                  {isPositioning && isDesktop ? (
                    <ImageEditor.DragZoomInfoPill />
                  ) : (
                    <div />
                  )}

                  <div className={styles.controls}>
                    <ImageEditor.PopOver
                      open={isBannerPopOverOpen}
                      onOpenChange={setIsBannerPopOverOpen}
                      placement="bottom-end"
                      content={
                        <BannerModalContent
                          imageLibrary={BANNER_IMAGE_LIBRARY}
                          displayMode="full"
                          onRemove={() => handleBannerChange(null)}
                          onBannerChange={handleBannerChange}
                          uploaderProps={{
                            accept: IMAGE_EDITOR_ACCEPT,
                            info: '8:1 ratio is recommended',
                          }}
                        />
                      }
                    >
                      <Button
                        variant="secondary"
                        onClick={() => setIsBannerPopOverOpen(true)}
                        disabled={state === 'saving'}
                        size="small"
                      >
                        {hasImage ? 'Edit banner' : 'Add banner'}
                      </Button>
                    </ImageEditor.PopOver>
                  </div>
                </div>
              }
            />
          </div>
        )}
      </div>
    );
  },
);

export default BannerEditor;
